/**
 * AdminSettings — the "Admin" tab inside the act-as investor view (T-042).
 *
 * The investor view is the admin's default surface for a member now; this
 * tab collects the controls that have NO investor equivalent:
 *   • Profile & terms: rename, hurdle / split / cadence (real inline forms —
 *     the old drill-down's prompt() chains never completed, T-029)
 *   • Loops: assign / unassign (the one subscription lever, T-017) + pause
 *   • Family: sub-accounts (rename / deactivate / add)
 *   • Settlements: confirm / edit / reject / remove, waiver (T-038),
 *     payout (D-9), add-on-behalf — edit + add are real modals
 *
 * Everything the drill-down duplicated from the investor view (hero
 * waterfall, movements, value events, settlement LIST as a read surface)
 * deliberately does NOT live here — the investor tabs are the read surface,
 * this tab is the control room.
 *
 * Mounted only in the act-as branch (App.jsx passes actingAsAdmin); a real
 * investor navigating to #admin-settings gets the guard card, and RLS
 * blocks every write here for non-admins regardless.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { getSupabase } from '../lib/supabase.js';
import './home.css';

const fmt$ = (n) => '$' + (Math.round(Math.abs(Number(n) || 0))).toLocaleString();
const fmtC = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return String(d).slice(0, 10); }
};

const MEMBER_PALETTE = ['#4f46e5', '#0ea5e9', '#f59e0b', '#ec4899', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6', '#a855f7', '#06b6d4'];
function memberColor(id) {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return MEMBER_PALETTE[h % MEMBER_PALETTE.length];
}
const initial = (n) => (n || '?').trim().charAt(0).toUpperCase() || '?';

// Attribution pill — same signal as the investor's own history rows.
function ActorPill({ role }) {
  if (role === 'admin') {
    return <span className="ast-actor-pill ast-actor-admin" title="Entered by an admin (you) on the investor's behalf">✎ by admin</span>;
  }
  if (role === 'executor') {
    return <span className="ast-actor-pill ast-actor-self" title="Entered by the investor">by investor</span>;
  }
  return null;
}

const lblStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--text-3)' };
const inpStyle = { font: 'inherit', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text-1)' };

// T-038: record a D18 typed waiver — ported verbatim from the retired
// drill-down. Books confirmed directly with a mandatory investor-visible memo.
function WaiverModal({ memberName, outstanding, saving, onClose, onSave }) {
  const [amount, setAmount] = useState(String(Math.max(0, Number(outstanding || 0)).toFixed(2)));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [memo, setMemo] = useState('');
  const [error, setError] = useState(null);

  const amt = Number(amount);
  const after = Number(outstanding || 0) - (Number.isFinite(amt) ? amt : 0);

  const submit = () => {
    if (!Number.isFinite(amt) || amt <= 0) { setError('Enter a positive amount.'); return; }
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) { setError('Pick a date.'); return; }
    if (!memo.trim()) { setError('A memo is required — the investor sees it on their history. Say why the debt is waived.'); return; }
    setError(null);
    onSave(amt, date, memo.trim());
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Record a waiver"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', width: '100%', maxWidth: 460, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 18px 50px rgba(2,6,23,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--accent)' }}>Record waiver</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, margin: '2px 0 0' }}>Waive debt for {memberName}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-3)', lineHeight: 1, padding: 4 }}>✕</button>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
          A waiver is a settlement record with <strong>no cash</strong> (D18): it pays down what {memberName} owes
          without ever counting as money received, and the profit clock does not move. It books
          <strong> confirmed immediately</strong> — waiving is your act, there is nothing for them to approve.
        </p>
        {error && <div style={{ background: 'var(--rose-bg,#fef2f2)', border: '1px solid var(--rose-border,#fecaca)', color: 'var(--rose)', padding: '9px 11px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ ...lblStyle, flex: 1 }}>Amount $
              <input type="number" min="0.01" step="0.01" value={amount} disabled={saving} onChange={(e) => setAmount(e.target.value)} onWheel={(e) => e.currentTarget.blur()} style={inpStyle} />
            </label>
            <label style={{ ...lblStyle, flex: 1 }}>Date
              <input type="date" value={date} disabled={saving} onChange={(e) => setDate(e.target.value)} style={inpStyle} />
            </label>
          </div>
          <label style={lblStyle}>Memo — investor-visible, required
            <textarea rows={3} value={memo} disabled={saving} placeholder={'e.g. Waiving the value of your ' + date + ' points redemption — goodwill, no payment needed.'}
              onChange={(e) => setMemo(e.target.value)} style={{ ...inpStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </label>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', background: 'var(--surface-2)', border: '1px dashed var(--border)', borderRadius: 8, padding: '8px 11px' }}>
            Outstanding {fmt$(outstanding)} → {after >= 0
              ? <strong>{fmt$(after)}</strong>
              : <><strong>$0</strong> with a <strong style={{ color: 'var(--green)' }}>{fmt$(-after)} credit</strong> (absorbed by their next rewards)</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving} style={{ padding: '9px 16px' }}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={saving} style={{ padding: '9px 16px', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Booking…' : 'Book waiver'}
          </button>
        </div>
      </div>
    </div>
  );
}

// D-9: pay out a credit — ported verbatim from the retired drill-down.
function PayoutModal({ memberName, credit, saving, onClose, onSave }) {
  const [amount, setAmount] = useState(String(Math.max(0, Number(credit || 0)).toFixed(2)));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState('cash');          // 'cash' | 'pool'
  const [method, setMethod] = useState('Zelle');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState(null);

  const amt = Number(amount);
  const after = Number(credit || 0) - (Number.isFinite(amt) ? amt : 0);
  const over = Number.isFinite(amt) && amt > Number(credit || 0) + 0.005;

  const submit = () => {
    if (!Number.isFinite(amt) || amt <= 0) { setError('Enter a positive amount.'); return; }
    if (over) { setError(`That exceeds the current credit (${fmtC(credit)}) — a payout settles the credit, never more.`); return; }
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) { setError('Pick a date.'); return; }
    if (!memo.trim()) { setError('A memo is required — the investor sees it on their history. Say what this payout covers.'); return; }
    setError(null);
    onSave(amt, date, mode === 'pool' ? 'Pool credit' : method, memo.trim(), mode === 'pool');
  };

  const modeBtn = (active) => ({
    flex: 1, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
    border: '1px solid ' + (active ? 'var(--green-border, #bbf7d0)' : 'var(--border)'),
    background: active ? 'var(--green-bg, #f0fdf4)' : 'var(--surface)',
    color: active ? 'var(--green)' : 'var(--text-2)', font: 'inherit',
  });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Pay out credit"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 18px 50px rgba(2,6,23,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--green)' }}>Pay out credit</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, margin: '2px 0 0' }}>Settle what you owe {memberName}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-3)', lineHeight: 1, padding: 4 }}>✕</button>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
          You currently owe {memberName} <strong style={{ color: 'var(--green)' }}>{fmtC(credit)}</strong>. A payout books
          <strong> confirmed immediately</strong> — paying is your act. It settles the credit and never touches
          the profit clock. The live credit moves with their rewards, so the figure here is prefilled fresh.
        </p>
        {error && <div style={{ background: 'var(--rose-bg,#fef2f2)', border: '1px solid var(--rose-border,#fecaca)', color: 'var(--rose)', padding: '9px 11px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={modeBtn(mode === 'cash')} onClick={() => setMode('cash')} disabled={saving}>
              <div style={{ fontWeight: 800, fontSize: 13 }}>Cash out</div>
              <div style={{ fontSize: 11.5, marginTop: 2 }}>Money actually leaves — Zelle, Mercury, wire…</div>
            </button>
            <button type="button" style={modeBtn(mode === 'pool')} onClick={() => setMode('pool')} disabled={saving}>
              <div style={{ fontWeight: 800, fontSize: 13 }}>Convert to pool credit</div>
              <div style={{ fontSize: 11.5, marginTop: 2 }}>No cash moves — it becomes TK-C draw power</div>
            </button>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ ...lblStyle, flex: 1 }}>Amount $
              <input type="number" min="0.01" step="0.01" value={amount} disabled={saving} onChange={(e) => setAmount(e.target.value)} onWheel={(e) => e.currentTarget.blur()} style={inpStyle} />
            </label>
            <label style={{ ...lblStyle, flex: 1 }}>Date
              <input type="date" value={date} disabled={saving} onChange={(e) => setDate(e.target.value)} style={inpStyle} />
            </label>
            {mode === 'cash' && (
              <label style={{ ...lblStyle, flex: 1 }}>Method
                <select value={method} disabled={saving} onChange={(e) => setMethod(e.target.value)} style={inpStyle}>
                  {['Zelle', 'Mercury', 'Wire', 'Coinbase', 'Cash', 'Other'].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
            )}
          </div>
          <label style={lblStyle}>Memo — investor-visible, required
            <textarea rows={3} value={memo} disabled={saving} placeholder={mode === 'pool'
              ? 'e.g. Converting your credit into pool balance — it now funds TK-C draws.'
              : 'e.g. Paying out your credit via Zelle.'}
              onChange={(e) => setMemo(e.target.value)} style={{ ...inpStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </label>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', background: 'var(--surface-2)', border: '1px dashed var(--border)', borderRadius: 8, padding: '8px 11px' }}>
            You owe {memberName} {fmtC(credit)} → <strong>{fmtC(Math.max(0, after))}</strong>
            {mode === 'pool' && Number.isFinite(amt) && amt > 0 && <> · their pool balance rises by <strong style={{ color: 'var(--green)' }}>{fmtC(amt)}</strong></>}
            {over && <span style={{ color: 'var(--rose)' }}> — exceeds the credit; the booking will be refused</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving} style={{ padding: '9px 16px' }}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={saving} style={{ padding: '9px 16px', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Booking…' : (mode === 'pool' ? 'Convert to pool credit' : 'Book payout')}
          </button>
        </div>
      </div>
    </div>
  );
}

// One real form for BOTH admin settlement flows the drill-down ran as
// prompt() chains that never completed (T-029: Chrome's dialog suppression
// nulls prompt #2+ and the whole edit silently aborts):
//   • add-on-behalf (row == null): the investor paid out-of-band
//   • edit (row != null): any status; a change to a confirmed row appends
//     the T-024 investor-visible "Edited by Kareem" memo trail
const METHOD_OPTIONS = ['Mercury', 'Zelle', 'Wire', 'Coinbase', 'Cash', 'Taekus pay', 'Other'];
function SettlementFormModal({ memberName, row, saving, onClose, onSave }) {
  const isEdit = !!row;
  const knownMethod = isEdit && METHOD_OPTIONS.includes(row.payment_method || '');
  const [amount, setAmount] = useState(isEdit ? String(Number(row.amount || 0)) : '');
  const [date, setDate] = useState(isEdit ? (row.settle_date || '').slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [methodSel, setMethodSel] = useState(isEdit ? (knownMethod ? row.payment_method : 'Other') : 'Mercury');
  const [methodOther, setMethodOther] = useState(isEdit && !knownMethod ? (row.payment_method || '') : '');
  const [memo, setMemo] = useState(isEdit ? (row.memo || '') : '');
  const [error, setError] = useState(null);

  const submit = () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setError('Enter a positive amount.'); return; }
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) { setError('Pick a date.'); return; }
    const method = methodSel === 'Other' ? methodOther.trim() : methodSel;
    if (!method) { setError('Pick or type a payment method.'); return; }
    setError(null);
    onSave({ amount: amt, date, method, memo });
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label={isEdit ? 'Edit payment' : 'Add settlement on behalf'}
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', width: '100%', maxWidth: 440, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 18px 50px rgba(2,6,23,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--accent)' }}>{isEdit ? 'Edit payment' : 'Add on behalf'}</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, margin: '2px 0 0' }}>{isEdit ? `${fmtC(row.amount)} · ${fmtDate(row.settle_date)}` : `Log a payment for ${memberName}`}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-3)', lineHeight: 1, padding: 4 }}>✕</button>
        </div>
        {isEdit && (row.status || 'pending') === 'confirmed' && (
          <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
            This payment is <strong>confirmed</strong> — saving a change appends an investor-visible
            "Edited by Kareem" note to its memo (T-024).
          </p>
        )}
        {!isEdit && (
          <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Use this when {memberName} paid out-of-band. The row lands in your pending queue and pays
            down outstanding once you confirm it.
          </p>
        )}
        {error && <div style={{ background: 'var(--rose-bg,#fef2f2)', border: '1px solid var(--rose-border,#fecaca)', color: 'var(--rose)', padding: '9px 11px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ ...lblStyle, flex: 1 }}>Amount $
              <input type="number" min="0.01" step="0.01" value={amount} disabled={saving} onChange={(e) => setAmount(e.target.value)} onWheel={(e) => e.currentTarget.blur()} style={inpStyle} />
            </label>
            <label style={{ ...lblStyle, flex: 1 }}>Date
              <input type="date" value={date} disabled={saving} onChange={(e) => setDate(e.target.value)} style={inpStyle} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ ...lblStyle, flex: 1 }}>Method
              <select value={methodSel} disabled={saving} onChange={(e) => setMethodSel(e.target.value)} style={inpStyle}>
                {METHOD_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            {methodSel === 'Other' && (
              <label style={{ ...lblStyle, flex: 1 }}>Custom method
                <input type="text" value={methodOther} disabled={saving} onChange={(e) => setMethodOther(e.target.value)} placeholder="e.g. PayPal" style={inpStyle} />
              </label>
            )}
          </div>
          <label style={lblStyle}>Memo — optional, investor-visible
            <textarea rows={2} value={memo} disabled={saving} onChange={(e) => setMemo(e.target.value)} style={{ ...inpStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving} style={{ padding: '9px 16px' }}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={saving} style={{ padding: '9px 16px', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Log payment')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Inline name editor — click Rename, the label becomes an input, Enter/Save
// commits. Single-field, zero dialogs.
function InlineName({ value, saving, onSave, bold = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ fontWeight: bold ? 800 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
        <button type="button" className="btn-ghost-pill" disabled={saving} onClick={() => setEditing(true)}>Rename</button>
      </span>
    );
  }
  const commit = () => {
    const t = draft.trim();
    if (t && t !== value) onSave(t);
    setEditing(false);
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <input
        type="text" value={draft} autoFocus disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
        style={{ ...inpStyle, padding: '6px 9px', fontSize: 13.5, width: 180 }}
      />
      <button type="button" className="btn-primary-sm" disabled={saving} onClick={commit}>Save</button>
      <button type="button" className="btn-ghost-pill" disabled={saving} onClick={() => { setDraft(value || ''); setEditing(false); }}>Cancel</button>
    </span>
  );
}

export default function AdminSettings({ member, profile, navigate, actingAsAdmin = false }) {
  const memberId = member?.id;
  const isAdmin = profile?.role === 'admin';
  const [data, setData] = useState({ status: 'loading', error: null });
  const [saving, setSaving] = useState(false);
  const [savingMsg, setSavingMsg] = useState(null);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [settleForm, setSettleForm] = useState(null);   // null | {row: null|settlement}
  const [terms, setTerms] = useState(null);             // enrollment draft {hurdle, splitPct, cadence}

  const load = useCallback(async () => {
    setData({ status: 'loading', error: null });
    const sb = getSupabase();
    if (!sb || !memberId) { setData({ status: 'error', error: new Error('No member in context') }); return; }
    try {
      const [enrollRes, loopsRes, subsRes, assignsRes, subAcctsRes, settlesRes] = await Promise.all([
        sb.from('module_enrollment').select('*').eq('member_id', memberId).eq('module_name', 'investment').maybeSingle(),
        sb.from('loops').select('id, name, active').eq('active', true),
        sb.from('loop_subscriptions').select('id, loop_id, paused, active').eq('enrolled_member_id', memberId).eq('active', true),
        sb.from('loop_assignments').select('id, loop_id, member_id, assigned_at').eq('member_id', memberId),
        sb.from('members').select('id, name, display_name, managed_by_member_id, active').eq('managed_by_member_id', memberId).eq('active', true),
        sb.from('investment_settlements').select('*').eq('enrolled_member_id', memberId).order('settle_date', { ascending: false }),
      ]);
      const errs = [enrollRes, loopsRes, subsRes, assignsRes, subAcctsRes, settlesRes].filter((r) => r.error);
      if (errs.length) throw new Error(errs.map((r) => r.error.message).join(' · '));

      // Contract summary — feeds the waiver/payout prefills. Tolerant: the
      // tab still manages loops/family/terms when the RPC is unreachable.
      let summary = null;
      try {
        const { data: s, error: sErr } = await sb.rpc('v2_household_summary', { p_member_id: memberId });
        if (!sErr && s && typeof s === 'object' && s.holder) summary = s;
      } catch { /* tolerated */ }

      setData({
        status: 'ready',
        enrollment: enrollRes.data || null,
        loops: loopsRes.data || [],
        subs: subsRes.data || [],
        assigns: assignsRes.data || [],
        subAccounts: subAcctsRes.data || [],
        settles: settlesRes.data || [],
        summary,
        error: null,
      });
    } catch (err) {
      console.error('[AdminSettings] load failed', err);
      setData({ status: 'error', error: err });
    }
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  // Seed the terms form from the loaded enrollment config.
  useEffect(() => {
    if (data.status !== 'ready') return;
    const cfg = data.enrollment?.config || {};
    setTerms({
      hurdle: String(Number(cfg.kareem_initial_capital || 0)),
      splitPct: String(Math.round(Number(cfg.split_after_hurdle ?? 0.5) * 100)),
      cadence: cfg.settlement_cadence || 'monthly',
    });
  }, [data.status, data.enrollment]);

  const sb = getSupabase();
  const flash = (msg) => { setSavingMsg(msg); setTimeout(() => setSavingMsg(null), 2400); };
  const reloadAndNotify = async () => {
    await load();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('finops:enrollment-changed', { detail: { memberId } }));
    }
  };

  // The tab renders only for the admin acting as this investor. A real
  // investor reaching #admin-settings sees the guard; RLS is the real wall.
  if (!actingAsAdmin || !isAdmin) {
    return (
      <div className="app density-comfortable"><main className="page">
        <div className="card" style={{ color: 'var(--text-2)' }}>
          <strong>Admin only.</strong>
          <p style={{ marginTop: 8, fontSize: 13 }}>This tab holds Kareem's controls for the household. <a href="#home">Back to Home</a>.</p>
        </div>
      </main></div>
    );
  }

  if (data.status === 'loading') {
    return <div className="app density-comfortable"><main className="page"><div className="card" style={{ color: 'var(--text-2)' }}>Loading admin settings…</div></main></div>;
  }
  if (data.status === 'error') {
    return (
      <div className="app density-comfortable"><main className="page">
        <div className="card" style={{ borderColor: 'var(--rose-border)', background: 'var(--rose-bg)', color: 'var(--rose)' }}>
          <strong>Couldn't load admin settings.</strong>
          <p className="mono" style={{ fontSize: 12, marginTop: 8 }}>{String(data.error?.message || data.error)}</p>
        </div>
      </main></div>
    );
  }

  const { enrollment, loops, subs, assigns, subAccounts, settles } = data;
  const cfg = enrollment?.config || {};
  const memberName = member.display_name || member.name;
  const S = data.summary || null;
  const outstanding = S ? Number(S.outstanding || 0) : 0;
  const credit = S ? Number(S.credit || 0) : 0;

  const subByLoop = Object.fromEntries(subs.map((s) => [s.loop_id, s]));
  const assignedLoopIds = new Set(assigns.map((a) => a.loop_id));
  const unassignedLoops = loops.filter((l) => !assignedLoopIds.has(l.id));

  // D-9 direction split — payouts render separately from investor payments.
  const isPayoutRow = (r) => (r.direction || 'to_kareem') === 'to_investor';
  const settledTotal = settles
    .filter((r) => (r.status || 'pending') === 'confirmed' && (r.kind || 'cash') === 'cash' && !isPayoutRow(r))
    .reduce((s, r) => s + Number(r.amount || 0), 0);
  const paidOutTotal = settles
    .filter((r) => (r.status || 'pending') === 'confirmed' && isPayoutRow(r))
    .reduce((s, r) => s + Number(r.amount || 0), 0);

  // ─── Handlers ────────────────────────────────────────────────────────
  const saveTerms = async () => {
    const hurdle = Number(terms.hurdle);
    const splitPct = Number(terms.splitPct);
    if (!Number.isFinite(hurdle) || hurdle < 0) { alert('Hurdle must be a non-negative number.'); return; }
    if (!Number.isFinite(splitPct) || splitPct < 0 || splitPct > 100) { alert('Split must be 0–100%.'); return; }
    setSaving(true);
    try {
      const nextCfg = {
        ...cfg,
        kareem_initial_capital: hurdle,
        split_after_hurdle: Math.max(0, Math.min(1, splitPct / 100)),
        settlement_cadence: terms.cadence || 'monthly',
      };
      if (enrollment) {
        const { error } = await sb.from('module_enrollment').update({ config: nextCfg, updated_at: new Date().toISOString() }).eq('member_id', memberId).eq('module_name', 'investment');
        if (error) throw error;
      } else {
        const { error } = await sb.from('module_enrollment').insert({ member_id: memberId, module_name: 'investment', enabled: true, config: { kareem_recovered: 0, ...nextCfg } });
        if (error) throw error;
      }
      flash('Terms saved');
      await reloadAndNotify();
    } catch (e) { alert('Save failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  const termsDirty = terms && (
    Number(terms.hurdle) !== Number(cfg.kareem_initial_capital || 0) ||
    Number(terms.splitPct) !== Math.round(Number(cfg.split_after_hurdle ?? 0.5) * 100) ||
    (terms.cadence || 'monthly') !== (cfg.settlement_cadence || 'monthly')
  );

  const renameMember = async (mid, nextName) => {
    setSaving(true);
    try {
      const { error } = await sb.from('members').update({ display_name: nextName, name: nextName }).eq('id', mid);
      if (error) throw error;
      flash('Name updated');
      await reloadAndNotify();
    } catch (e) { alert('Rename failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  // T-017: assignment IS the subscription lever — the DB triggers create/
  // reactivate the subscription on assign and deactivate it on unassign.
  const assignLoop = async (loopId) => {
    if (!loopId) return;
    setSaving(true);
    try {
      const { error } = await sb.from('loop_assignments').insert({ loop_id: loopId, member_id: memberId });
      if (error) throw error;
      flash('Loop assigned — live for the household');
      await reloadAndNotify();
    } catch (e) { alert('Assign failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  const unassignLoop = async (assignment) => {
    const loopName = loops.find((l) => l.id === assignment.loop_id)?.name || assignment.loop_id;
    if (!confirm(`Unassign "${loopName}" from ${memberName}?\n\nThis also unsubscribes the household — the loop disappears from their app. History stays in the ledger. Re-assigning re-activates it.`)) return;
    setSaving(true);
    try {
      const { error } = await sb.from('loop_assignments').delete().eq('id', assignment.id);
      if (error) throw error;
      flash('Loop unassigned');
      await reloadAndNotify();
    } catch (e) { alert('Unassign failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  const togglePauseSub = async (sub) => {
    setSaving(true);
    try {
      const { error } = await sb.from('loop_subscriptions').update({ paused: !sub.paused }).eq('id', sub.id);
      if (error) throw error;
      flash(sub.paused ? 'Loop resumed' : 'Loop paused');
      await reloadAndNotify();
    } catch (e) { alert('Save failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  const deactivateSubAccount = async (subAcct) => {
    const subName = subAcct.display_name || subAcct.name;
    if (!confirm(`Deactivate sub-account "${subName}"?\n\nHistory preserved. Removes them from the family grid.`)) return;
    setSaving(true);
    try {
      const { error } = await sb.from('members').update({ active: false }).eq('id', subAcct.id);
      if (error) throw error;
      flash('Sub-account deactivated');
      await reloadAndNotify();
    } catch (e) { alert('Deactivate failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  // ─── Settlement handlers (ported from the drill-down; edit/add are real
  // forms now — the prompt chains never completed, T-029) ────────────────
  const confirmSettlement = async (settlementId) => {
    setSaving(true);
    try {
      const { error } = await sb.from('investment_settlements').update({ status: 'confirmed' }).eq('id', settlementId);
      if (error) throw error;
      flash('Settlement confirmed');
      await reloadAndNotify();
    } catch (e) { alert('Confirm failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  const saveSettlementForm = async ({ amount, date, method, memo }) => {
    const row = settleForm?.row || null;
    setSaving(true);
    try {
      if (!row) {
        // Add on behalf — lands pending, exactly like the investor's own entry.
        const { error } = await sb.from('investment_settlements').insert({
          enrolled_member_id: memberId,
          amount, settle_date: date, payment_method: method || null,
          memo: memo || null,
        });
        if (error) throw error;
        flash('Settlement logged');
      } else {
        // T-024: edits to a confirmed row append an investor-visible trail.
        const wasConfirmed = (row.status || 'pending') === 'confirmed';
        const changes = [];
        if (amount !== Number(row.amount || 0)) changes.push(`amount ${fmt$(Number(row.amount || 0))}→${fmt$(amount)}`);
        if (date !== row.settle_date) changes.push(`date ${row.settle_date || '—'}→${date}`);
        if ((method || '') !== (row.payment_method || '')) changes.push(`method ${row.payment_method || '—'}→${method}`);
        const nextMemo = (wasConfirmed && changes.length)
          ? (memo ? memo + ' | ' : '') + 'Edited by Kareem ' + new Date().toISOString().slice(0, 10) + ' (' + changes.join(', ') + ')'
          : memo;
        const { error } = await sb.from('investment_settlements')
          .update({ amount, settle_date: date, payment_method: method || null, memo: nextMemo || null })
          .eq('id', row.id);
        if (error) throw error;
        flash('Payment updated');
      }
      setSettleForm(null);
      await reloadAndNotify();
    } catch (e) { alert('Save failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  // T-024: remove/reject = SOFT (status rejected + note), never a hard
  // DELETE. One optional-note prompt — single dialogs complete reliably.
  const adminRemoveSettlement = async (r) => {
    const wasConfirmed = (r.status || 'pending') === 'confirmed';
    const reason = prompt(`${wasConfirmed ? 'Remove' : 'Reject'} this ${fmt$(Number(r.amount || 0))} payment? Optional note to the investor:`, '');
    if (reason == null) return;
    const memo = (r.memo ? r.memo + ' | ' : '') +
      (wasConfirmed ? 'Removed by Kareem ' : 'Rejected by Kareem ') + new Date().toISOString().slice(0, 10) +
      (reason.trim() ? ': ' + reason.trim() : '');
    setSaving(true);
    try {
      const { error } = await sb.from('investment_settlements').update({ status: 'rejected', memo }).eq('id', r.id);
      if (error) throw error;
      flash(wasConfirmed ? 'Payment removed from the books' : 'Payment rejected');
      await reloadAndNotify();
    } catch (e) { alert('Remove failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  const recordWaiver = async (amount, date, memo) => {
    setSaving(true);
    try {
      const { error } = await sb.from('investment_settlements').insert({
        enrolled_member_id: memberId,
        amount,
        settle_date: date,
        payment_method: 'Waiver — no cash',
        memo: 'WAIVER — NOT A PAYMENT. ' + memo,
        status: 'confirmed',
        notify_kareem: true,
        direction: 'to_kareem',
        kind: 'waiver',
      });
      if (error) throw error;
      setWaiverOpen(false);
      flash('Waiver booked — no cash recorded, outstanding reduced');
      await reloadAndNotify();
    } catch (e) { alert('Waiver failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  const recordPayout = async (amount, date, method, memo, asPoolCredit) => {
    setSaving(true);
    try {
      const { data: res, error } = await sb.rpc('v2_record_investor_payout', {
        p_member_id: memberId,
        p_amount: amount,
        p_settle_date: date,
        p_method: method,
        p_memo: memo,
        p_as_pool_credit: asPoolCredit,
      });
      if (error) throw error;
      setPayoutOpen(false);
      flash(asPoolCredit
        ? `Converted ${fmtC(amount)} to pool credit — their TK-C draw power rose by the same amount`
        : `Payout booked — you owe ${fmtC(Number(res?.creditAfter ?? 0))} now`);
      await reloadAndNotify();
    } catch (e) { alert('Payout failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  // ─── Render ──────────────────────────────────────────────────────────
  const color = memberColor(member.id);
  return (
    <div className="app density-comfortable">
      <main className="page">
        {/* Identity header */}
        <div className="page-title-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 20, flexShrink: 0 }}>
              {initial(memberName)}
            </div>
            <div>
              <div className="page-eyebrow"><span className="dot"></span> Admin settings</div>
              <h1 className="page-title" style={{ margin: 0 }}>{memberName}</h1>
              <p className="page-sub" style={{ marginTop: 4 }}>
                Your controls for this household — everything else lives in the investor tabs.
                {savingMsg && <span style={{ marginLeft: 12, color: 'var(--green)', fontWeight: 600 }}>✓ {savingMsg}</span>}
              </p>
            </div>
          </div>
        </div>

        {/* Profile & terms */}
        <section className="card">
          <header className="card-header">
            <div className="card-title-block">
              <div className="card-eyebrow">Profile & terms</div>
              <h2 className="card-title">Enrollment</h2>
              <p className="card-desc">
                {enrollment ? <>Enrolled · settles {cfg.settlement_cadence || 'monthly'}.</> : <span style={{ color: 'var(--amber)' }}>Not enrolled — saving terms below enrolls the household.</span>}
                {' '}Changing terms never rewrites history (T-002): past events keep their posted rates.
              </p>
            </div>
          </header>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-3)', width: 110, flexShrink: 0 }}>Display name</span>
              <InlineName value={memberName} saving={saving} bold onSave={(next) => renameMember(member.id, next)} />
            </div>
            {terms && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <label style={{ ...lblStyle, width: 150 }}>Hurdle $ — capital to recover
                  <input type="number" min="0" step="any" value={terms.hurdle} disabled={saving} onChange={(e) => setTerms({ ...terms, hurdle: e.target.value })} onWheel={(e) => e.currentTarget.blur()} style={inpStyle} />
                </label>
                <label style={{ ...lblStyle, width: 150 }}>Kareem split after hurdle %
                  <input type="number" min="0" max="100" step="1" value={terms.splitPct} disabled={saving} onChange={(e) => setTerms({ ...terms, splitPct: e.target.value })} onWheel={(e) => e.currentTarget.blur()} style={inpStyle} />
                </label>
                <label style={{ ...lblStyle, width: 150 }}>Settlement cadence
                  <select value={terms.cadence} disabled={saving} onChange={(e) => setTerms({ ...terms, cadence: e.target.value })} style={inpStyle}>
                    <option value="monthly">monthly</option>
                    <option value="quarterly">quarterly</option>
                    <option value="on_demand">on demand</option>
                  </select>
                </label>
                <button type="button" className="btn-primary-sm" disabled={saving || !termsDirty} onClick={saveTerms} style={{ opacity: termsDirty ? 1 : 0.5 }}>
                  {saving ? 'Saving…' : 'Save terms'}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Loops — assignment is the one lever (T-017) */}
        <section className="card">
          <header className="card-header">
            <div className="card-title-block">
              <div className="card-eyebrow">Loops</div>
              <h2 className="card-title">What loops can {memberName} use?</h2>
              <p className="card-desc">Members only see loops you assign. Assigning makes the loop live for the household immediately; unassigning removes it (history stays). Pause covers temporary stops.</p>
            </div>
            {unassignedLoops.length > 0 && (
              <select className="ast-assign-picker" value="" disabled={saving} onChange={(e) => { assignLoop(e.target.value); e.target.value = ''; }}>
                <option value="">+ Assign loop…</option>
                {unassignedLoops.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </header>
          {assigns.length === 0 ? (
            <div className="ast-empty-card">No loops assigned yet — pick one from "+ Assign loop…".</div>
          ) : (
            <div className="ast-list">
              {assigns.map((a) => {
                const loopName = loops.find((l) => l.id === a.loop_id)?.name || a.loop_id;
                const sub = subByLoop[a.loop_id];
                return (
                  <div key={a.id} className="ast-row">
                    <div className="ast-row-main">
                      <span style={{ fontWeight: 700 }}>{loopName}</span>
                      {sub && !sub.paused && <span className="ast-pill ast-pill-ok">live</span>}
                      {sub && sub.paused && <span className="ast-pill ast-pill-amber">paused</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {sub && (
                        <button type="button" className="btn-ghost-pill" disabled={saving} onClick={() => togglePauseSub(sub)}>
                          {sub.paused ? 'Resume' : 'Pause'}
                        </button>
                      )}
                      <button type="button" className="btn-ghost-pill" style={{ color: 'var(--rose)' }} disabled={saving} onClick={() => unassignLoop(a)}>Unassign</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Family */}
        <section className="card">
          <header className="card-header">
            <div className="card-title-block">
              <div className="card-eyebrow">Family</div>
              <h2 className="card-title">{subAccounts.length} sub-account{subAccounts.length === 1 ? '' : 's'}</h2>
              <p className="card-desc">Managed by {memberName}. Each sub-account rolls its ledger up to this household.</p>
            </div>
            <button type="button" className="btn-primary-sm" disabled={saving} onClick={() => navigate && navigate('add-subaccount')}>
              ＋ Add sub-account
            </button>
          </header>
          {subAccounts.length === 0 ? (
            <div className="ast-empty-card">No sub-accounts yet.</div>
          ) : (
            <div className="ast-list">
              {subAccounts.map((s) => (
                <div key={s.id} className="ast-row">
                  <div className="ast-row-main">
                    <span className="ast-avatar" style={{ background: memberColor(s.id) }}>{initial(s.display_name || s.name)}</span>
                    <InlineName value={s.display_name || s.name} saving={saving} onSave={(next) => renameMember(s.id, next)} />
                  </div>
                  <button type="button" className="btn-ghost-pill" style={{ color: 'var(--rose)' }} disabled={saving} onClick={() => deactivateSubAccount(s)}>Deactivate</button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Settlements — the admin actions; the investor-facing list lives on
            their Settlements screen (this card is the control room, not a
            second read surface, so it renders compact rows). */}
        <section className="card">
          <header className="card-header">
            <div className="card-title-block">
              <div className="card-eyebrow">Settlements</div>
              <h2 className="card-title">
                {settles.length} on record · {fmt$(settledTotal)} paid by them
                {paidOutTotal > 0 && <> · <span style={{ color: 'var(--green)' }}>{fmt$(paidOutTotal)} paid out by you</span></>}
              </h2>
              <p className="card-desc">Payments both ways. Add one on behalf of {memberName} if they paid out-of-band{credit > 0 ? ', or pay out the credit you owe them' : ''}.</p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {S && credit > 0 && (
                <button type="button" className="btn-primary-sm" onClick={() => setPayoutOpen(true)} disabled={saving}
                  style={{ background: 'var(--green)', borderColor: 'var(--green)' }}>
                  Pay out {fmt$(credit)} →
                </button>
              )}
              <button type="button" className="btn-ghost" onClick={() => setWaiverOpen(true)} disabled={saving} style={{ fontSize: 12.5 }}>Record waiver</button>
              <button type="button" className="btn-primary-sm" onClick={() => setSettleForm({ row: null })} disabled={saving}>+ Add on behalf</button>
            </div>
          </header>
          {settles.length === 0 ? (
            <div className="ast-empty-card">No settlements yet.</div>
          ) : (
            <div className="ast-settle-list">
              {settles.map((r) => {
                const status = r.status || 'pending';
                const isPayout = isPayoutRow(r);
                return (
                  <div key={r.id} className={'ast-settle-row' + (status === 'pending' ? ' is-pending' : '') + (status === 'rejected' ? ' is-rejected' : '')}>
                    <div className="ast-settle-date">{fmtDate(r.settle_date)}</div>
                    <div className="ast-settle-method">
                      {r.payment_method || 'Other'}
                      {isPayout && <span className="ast-payout-pill" title={`You paid this to ${memberName}` + (r.payment_method === 'Pool credit' ? ' as pool balance — no cash moved' : '')}>you → {memberName}</span>}
                    </div>
                    <div className="ast-settle-amt mono" style={isPayout ? { color: 'var(--green)' } : undefined}>{isPayout ? '−' : ''}{fmt$(r.amount)}</div>
                    <div className="ast-settle-memo">
                      <ActorPill role={r.created_by_role} />
                      {' '}{r.memo || <span style={{ color: 'var(--text-3)' }}>—</span>}
                    </div>
                    <div className="ast-settle-status">
                      <span className={'ast-status-pill ' + status}>{status}</span>
                      {status === 'pending' && (
                        <button type="button" className="btn-primary-sm" disabled={saving} onClick={() => confirmSettlement(r.id)}>Confirm</button>
                      )}
                      {status !== 'rejected' && (
                        <>
                          <button type="button" className="btn-ghost" style={{ fontSize: 12, padding: '4px 8px' }} disabled={saving} onClick={() => setSettleForm({ row: r })}>Edit</button>
                          <button type="button" className="btn-ghost" style={{ fontSize: 12, padding: '4px 8px', color: 'var(--rose)' }} disabled={saving} onClick={() => adminRemoveSettlement(r)}>
                            {status === 'pending' ? 'Reject' : 'Remove'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <footer className="page-foot">
          All writes use your admin RLS and are attributed to you. The investor sees changes on next load (or immediately via the enrollment-changed event).
        </footer>
      </main>

      {waiverOpen && (
        <WaiverModal memberName={memberName} outstanding={outstanding} saving={saving} onClose={() => setWaiverOpen(false)} onSave={recordWaiver} />
      )}
      {payoutOpen && (
        <PayoutModal memberName={memberName} credit={credit} saving={saving} onClose={() => setPayoutOpen(false)} onSave={recordPayout} />
      )}
      {settleForm && (
        <SettlementFormModal memberName={memberName} row={settleForm.row} saving={saving} onClose={() => setSettleForm(null)} onSave={saveSettlementForm} />
      )}

      <style>{`
        .ast-assign-picker {
          padding: 6px 12px;
          background: transparent;
          border: 1px dashed var(--border-2);
          border-radius: 999px;
          font-size: 12.5px; font-weight: 600;
          color: var(--text-2);
          cursor: pointer;
          flex-shrink: 0;
        }
        .ast-empty-card {
          padding: 20px; text-align: center; color: var(--text-3); font-size: 13px;
          background: var(--surface-2); border-radius: var(--radius);
        }
        .ast-list { display: flex; flex-direction: column; gap: 8px; }
        .ast-row {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          padding: 10px 14px;
          border: 1px solid var(--border); border-radius: var(--radius-sm);
          background: var(--surface); font-size: 13.5px;
        }
        .ast-row-main { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .ast-avatar {
          width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
          display: inline-flex; align-items: center; justify-content: center;
          color: #fff; font-weight: 700; font-size: 13px;
        }
        .ast-pill {
          font-size: 9.5px; font-weight: 800; letter-spacing: 0.06em;
          text-transform: uppercase; padding: 2px 8px; border-radius: 999px;
        }
        .ast-pill-ok { background: var(--green-bg); color: var(--green); border: 1px solid var(--green-border); }
        .ast-pill-amber { background: var(--amber-bg); color: var(--amber); border: 1px solid var(--amber-border); }

        .ast-settle-list { display: flex; flex-direction: column; gap: 6px; }
        .ast-settle-row {
          display: grid; grid-template-columns: 110px 110px auto 1fr auto; gap: 10px;
          align-items: center;
          padding: 8px 12px;
          border: 1px solid var(--border); border-radius: var(--radius-sm);
          background: var(--surface); font-size: 13px;
        }
        .ast-settle-row.is-pending {
          border-color: var(--amber-border, #fcd34d);
          background: var(--amber-bg, #fef3c7);
        }
        .ast-settle-row.is-rejected { opacity: 0.55; }
        .ast-settle-date { font-size: 11px; color: var(--text-3); font-weight: 600; }
        .ast-settle-method { font-weight: 700; color: var(--text-1); }
        .ast-settle-amt { color: var(--text-1); font-weight: 700; font-variant-numeric: tabular-nums; }
        .ast-settle-memo { font-size: 12.5px; color: var(--text-2); overflow-wrap: anywhere; }
        .ast-settle-status { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
        .ast-status-pill {
          font-size: 10px; font-weight: 800; padding: 3px 8px;
          border-radius: 999px; letter-spacing: 0.04em; text-transform: uppercase;
        }
        .ast-status-pill.pending { background: var(--amber-bg); color: var(--amber); border: 1px solid var(--amber-border); }
        .ast-status-pill.confirmed { background: var(--green-bg); color: var(--green); border: 1px solid var(--green-border); }
        .ast-status-pill.rejected { background: var(--surface-2); color: var(--text-3); border: 1px solid var(--border); text-decoration: line-through; }
        .ast-payout-pill {
          display: inline-flex; align-items: center; margin-left: 8px;
          font-size: 9.5px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase;
          padding: 2px 7px; border-radius: 999px; white-space: nowrap; vertical-align: middle;
          background: var(--green-bg); color: var(--green); border: 1px solid var(--green-border);
        }
        .ast-actor-pill {
          display: inline-flex; align-items: center;
          font-size: 9.5px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase;
          padding: 2px 6px; border-radius: 999px; white-space: nowrap;
          vertical-align: middle;
        }
        .ast-actor-admin { background: var(--amber-bg); color: var(--amber); border: 1px solid var(--amber-border); }
        .ast-actor-self { background: var(--surface-2); color: var(--text-3); border: 1px solid var(--border); }

        /* Phone: settlement rows collapse into stacked cards. */
        @media (max-width: 560px) {
          .ast-settle-row { grid-template-columns: 1fr auto; row-gap: 4px; }
          .ast-settle-memo { grid-column: 1 / -1; }
          .ast-settle-status { grid-column: 1 / -1; justify-content: flex-start; }
        }
      `}</style>
    </div>
  );
}
